import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import Logo from '@/components/Logo';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const NAV_ITEMS = [
  { to: '/', key: 'home' },
  { to: '/about', key: 'about' },
  { to: '/investments', key: 'investments' },
  { to: '/real-estate', key: 'real_estate' },
  { to: '/asset-management', key: 'asset_management' },
  { to: '/contact', key: 'contact' },
];

const LANG_LABELS = { lt: 'LT', en: 'EN', de: 'DE', ru: 'RU' };

const Header = () => {
  const { t, lang, setLang, supported } = useLanguage();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-black/80 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link">
          <Logo />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.to === '/'}
              data-testid={`nav-link-${item.key}`}
              className={({ isActive }) =>
                `link-underline whitespace-nowrap text-xs uppercase tracking-[0.2em] font-medium transition-colors ${
                  isActive ? 'text-gold active' : 'text-white/80 hover:text-white'
                }`
              }
            >
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          {/* Language switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="lang-switcher-trigger"
              className="flex items-center gap-1 text-xs tracking-[0.2em] uppercase text-white/80 hover:text-gold transition-colors"
            >
              {LANG_LABELS[lang]} <ChevronDown size={14} strokeWidth={1.5} />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-[#141414] border border-[#262626] rounded-none min-w-[80px]"
            >
              {supported.map((l) => (
                <DropdownMenuItem
                  key={l}
                  data-testid={`lang-option-${l}`}
                  onClick={() => setLang(l)}
                  className={`text-xs tracking-[0.2em] uppercase cursor-pointer focus:bg-[#1E1E1E] focus:text-gold ${
                    lang === l ? 'text-gold' : 'text-white/80'
                  }`}
                >
                  {LANG_LABELS[l]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Mobile menu toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {open && (
        <nav
          className="lg:hidden bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/10"
          data-testid="mobile-nav"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.to === '/'}
                data-testid={`mobile-nav-link-${item.key}`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.2em] ${
                    isActive ? 'text-gold' : 'text-white/80'
                  }`
                }
              >
                {t(`nav.${item.key}`)}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
